import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CveIntel, FindingAnalysis, PriorityLevel, Provenance } from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import { ScoreBadge } from './AiSuggestion';

/**
 * A finding's vulnerability intelligence, as the sources state it.
 *
 * Every fact is shown with where it came from and when. A score from NVD is labelled NVD; a score
 * from Guardian's rule table is labelled as Guardian's own, with its rationale and review state;
 * a finding with neither shows no score rather than an invented one. The priority is computed by
 * fixed rules over these facts, and its reasons are listed.
 */

const PRIORITY: Record<PriorityLevel, { label: string; cls: string }> = {
  ACT_NOW: { label: 'Act now', cls: 'prio-act-now' },
  HIGH: { label: 'High', cls: 'prio-high' },
  MEDIUM: { label: 'Medium', cls: 'prio-medium' },
  LOW: { label: 'Low', cls: 'prio-low' },
  INFORMATIONAL: { label: 'Informational', cls: 'prio-info' },
};

const SOURCE_LABEL: Record<Provenance['source'], string> = {
  NVD: 'NVD',
  CISA_KEV: 'CISA KEV',
  FIRST_EPSS: 'FIRST EPSS',
  MITRE_CWE: 'MITRE CWE',
  OSV: 'OSV',
  GUARDIAN_RULES: 'Guardian rule assessment',
  ZAP: 'ZAP',
};

const day = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '';

function Source({ p }: { p: Provenance }) {
  return (
    <span className="intel-source">
      {SOURCE_LABEL[p.source]}
      {p.reference ? ` · ${p.reference}` : ''}
      {p.fetchedAt ? ` · fetched ${day(p.fetchedAt)}` : ''}
    </span>
  );
}

/** The agent's grounded analysis of a finding, with the run it came from. */
export interface AiAnalysis {
  analysis: FindingAnalysis;
  confidence: number;
  promptVersion: string;
}

export function IntelPanel({
  engagementId,
  findingId,
  mayRefresh,
  ai,
}: {
  engagementId: string;
  findingId: string;
  mayRefresh: boolean;
  ai?: AiAnalysis | undefined;
}) {
  const queryClient = useQueryClient();
  const intel = useQuery({
    queryKey: ['intel', engagementId, findingId],
    queryFn: () => api.getFindingIntel(engagementId, findingId),
    // While CVEs are still being fetched, check back.
    refetchInterval: (q) =>
      q.state.data?.cves.some((c) => c.status === 'PENDING') ? 15_000 : false,
  });
  const refresh = useMutation({
    mutationFn: () => api.refreshFindingIntel(engagementId, findingId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['intel', engagementId, findingId] }),
  });

  if (intel.isLoading) return <p className="muted">Reading the knowledge base…</p>;
  if (intel.isError || !intel.data) {
    return (
      <p className="login-error" role="alert">
        {intel.error instanceof ApiError ? intel.error.message : 'Could not load the intelligence.'}
      </p>
    );
  }
  const d = intel.data;
  const canRefresh =
    mayRefresh && (d.classification.cveIds.length > 0 || Boolean(d.classification.component));

  return (
    <div className="intel">
      <div className="intel-head">
        <span className={`prio ${PRIORITY[d.priority.level].cls}`}>
          {PRIORITY[d.priority.level].label}
        </span>
        <ul className="intel-reasons">
          {d.priority.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
        {canRefresh ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            {refresh.isSuccess ? 'Requested' : 'Refresh from sources'}
          </button>
        ) : null}
      </div>

      {d.knowledgeBase === 'UNAVAILABLE' ? (
        <p className="demo-notice">
          The knowledge base is not available, so only what ships with the platform is shown — no
          NVD, EPSS, KEV or CWE facts.
        </p>
      ) : null}

      <h4 className="intel-title">Source facts</h4>
      <dl className="kv">
        <dt>Weakness</dt>
        <dd>
          {d.weakness ? (
            <>
              <a
                className="table-link"
                href={`https://cwe.mitre.org/data/definitions/${d.weakness.cweId}.html`}
                target="_blank"
                rel="noreferrer"
              >
                CWE-{d.weakness.cweId}
              </a>{' '}
              {d.weakness.name}
              {d.weakness.owaspTop10 ? (
                <span className="intel-owasp">
                  {' '}
                  · OWASP {d.weakness.owaspTop10.id} {d.weakness.owaspTop10.name}
                </span>
              ) : null}
              <br />
              <Source p={d.weakness.provenance} />
            </>
          ) : d.classification.cweId ? (
            `CWE-${d.classification.cweId} (catalog not loaded yet)`
          ) : (
            '—'
          )}
        </dd>

        <dt>Score</dt>
        <dd>
          {d.severity.cvss ? (
            <>
              <span className={`cvss cvss-${d.severity.cvss.severity.toLowerCase()}`}>
                CVSS {d.severity.cvss.version} {d.severity.cvss.baseScore.toFixed(1)}{' '}
                {d.severity.cvss.severity}
              </span>{' '}
              <span className="mono intel-vector">{d.severity.cvss.vector}</span>
              <br />
              {d.severity.basis === 'CVE' ? (
                <span className="intel-source">
                  Published by {d.severity.cvss.assigner ?? 'the source'} for {d.severity.cveId}
                </span>
              ) : (
                <>
                  <span className="intel-source">
                    Guardian rule assessment {d.severity.rulesVersion} — not an NVD score.{' '}
                    {d.severity.reviewed
                      ? `Reviewed by ${d.severity.reviewed.by}.`
                      : 'Pending security review.'}
                  </span>
                  {d.severity.rationale ? (
                    <p className="intel-rationale">{d.severity.rationale}</p>
                  ) : null}
                </>
              )}
            </>
          ) : (
            <>
              No CVSS: no CVE is linked and the rule has no assessment. Scanner risk{' '}
              {d.severity.scannerRisk}.
              {d.severity.rationale ? (
                <p className="intel-rationale">{d.severity.rationale}</p>
              ) : null}
            </>
          )}
        </dd>

        {d.classification.component ? (
          <>
            <dt>Component</dt>
            <dd className="mono">
              {d.classification.component.ecosystem}:{d.classification.component.name}@
              {d.classification.component.version}
            </dd>
          </>
        ) : null}
      </dl>

      {d.cves.length > 0 ? (
        <>
          <h4 className="intel-title">CVEs ({d.cves.length})</h4>
          <ul className="intel-cves">
            {d.cves.map((c) => (
              <CveCard key={c.cveId} cve={c} />
            ))}
          </ul>
        </>
      ) : (
        <p className="muted" style={{ fontSize: 13 }}>
          No CVE applies: this is a weakness in the application itself, not a known vulnerability in
          a product version.
        </p>
      )}
      {ai ? (
        <section className="intel-ai" aria-label="AI analysis">
          <h4 className="intel-title">
            <span className="ai-tag">AI</span> Analysis — derived from the facts above, not a source
          </h4>
          <p className="intel-source">
            Engagement Lead · {ai.promptVersion} · <ScoreBadge score={ai.confidence} /> · every
            citation checked against the knowledge base before it was stored
          </p>
          <dl className="kv">
            <dt>Summary</dt>
            <dd>{ai.analysis.summary}</dd>
            <dt>Attack scenario</dt>
            <dd>{ai.analysis.attackScenario}</dd>
            <dt>Business impact</dt>
            <dd>{ai.analysis.businessImpact}</dd>
            <dt>Recommended action</dt>
            <dd>{ai.analysis.recommendedAction}</dd>
            <dt>Rests on</dt>
            <dd className="mono">{ai.analysis.citations.join(' · ')}</dd>
          </dl>
        </section>
      ) : null}
    </div>
  );
}

function CveCard({ cve }: { cve: CveIntel }) {
  return (
    <li className="intel-cve">
      <div className="intel-cve-head">
        <a
          className="table-link mono"
          href={`https://nvd.nist.gov/vuln/detail/${cve.cveId}`}
          target="_blank"
          rel="noreferrer"
        >
          {cve.cveId}
        </a>
        {cve.kev ? <span className="prio prio-act-now">CISA KEV</span> : null}
        {cve.status !== 'LOADED' ? (
          <span className="muted">
            {cve.status === 'PENDING' ? 'fetching from NVD…' : 'not in NVD'}
          </span>
        ) : null}
      </div>
      {cve.description ? <p className="intel-desc">{cve.description}</p> : null}
      <dl className="kv">
        {cve.cvss.length > 0 ? (
          <>
            <dt>CVSS</dt>
            <dd>
              {cve.cvss.map((s) => (
                <div key={`${s.version}${s.assigner}${s.type}`}>
                  <span className={`cvss cvss-${s.severity.toLowerCase()}`}>
                    {s.version} {s.baseScore.toFixed(1)} {s.severity}
                  </span>{' '}
                  <span className="mono intel-vector">{s.vector}</span>{' '}
                  <span className="intel-source">
                    {s.assigner}
                    {s.type ? ` (${s.type})` : ''}
                  </span>
                </div>
              ))}
            </dd>
          </>
        ) : null}
        {cve.epss ? (
          <>
            <dt>EPSS</dt>
            <dd>
              {(cve.epss.score * 100).toFixed(2)}% chance of exploitation in 30 days · percentile{' '}
              {(cve.epss.percentile * 100).toFixed(0)} · {cve.epss.date}{' '}
              <Source p={cve.epss.provenance} />
            </dd>
          </>
        ) : null}
        {cve.kev ? (
          <>
            <dt>Exploited</dt>
            <dd>
              Added {cve.kev.dateAdded}
              {cve.kev.dueDate ? ` · due ${cve.kev.dueDate}` : ''}
              {cve.kev.knownRansomware === 'Known' ? ' · used in ransomware' : ''}
              {cve.kev.requiredAction ? (
                <p className="intel-rationale">{cve.kev.requiredAction}</p>
              ) : null}
              <Source p={cve.kev.provenance} />
            </dd>
          </>
        ) : null}
        {cve.fixedIn.length > 0 ? (
          <>
            <dt>Fixed in</dt>
            <dd className="mono">
              {[...new Set(cve.fixedIn.map((f) => `${f.package} ${f.fixed}`))].join(', ')}{' '}
              <span className="intel-source">
                OSV · {[...new Set(cve.fixedIn.map((f) => f.advisoryId))].join(', ')}
              </span>
            </dd>
          </>
        ) : null}
        {cve.cweIds.length > 0 ? (
          <>
            <dt>Weakness</dt>
            <dd className="mono">{cve.cweIds.join(', ')}</dd>
          </>
        ) : null}
      </dl>
      {cve.provenance ? <Source p={cve.provenance} /> : null}
    </li>
  );
}
